import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import StadiumShell from '../layouts/StadiumShell';
import { NAV_LINKS } from './content';

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-2.5 shrink-0 text-white hover:opacity-90 transition-opacity">
      <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-[11px] font-black italic text-black -rotate-6 shadow-[0_0_20px_rgba(16,185,129,0.5)]">
        LI
      </span>
      <span className="text-lg font-black tracking-tighter uppercase italic">Locked In</span>
    </Link>
  );
}

function PublicNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/5">
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <BrandMark />

        <div className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `text-[11px] font-black uppercase tracking-widest transition-colors ${
                  isActive ? 'text-emerald-400' : 'text-gray-400 hover:text-white'
                }`}
            >
              {label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to="/signup?mode=login"
            className="hidden sm:inline text-gray-400 hover:text-white font-bold px-3 py-2 text-[11px] uppercase tracking-wider transition-colors"
          >
            Log in
          </Link>
          <Link
            to="/app"
            className="hidden sm:inline-flex items-center px-4 py-2.5 rounded-full text-[11px] font-black uppercase tracking-wider border border-white/15 text-white hover:border-emerald-500/50 hover:bg-white/5 transition-all"
          >
            Enter as Guest
          </Link>
          <Link
            to="/signup"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-black px-4 sm:px-6 py-2.5 rounded-full text-[11px] uppercase tracking-wider transition-all hover:scale-105"
          >
            Create Account
          </Link>
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden h-9 w-9 rounded-lg border border-white/10 bg-black/40 text-gray-300 flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4" aria-hidden>
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-black/90 backdrop-blur-xl">
          <div className="px-4 py-3 flex flex-col">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className="py-2.5 text-xs font-black uppercase tracking-widest text-gray-300 hover:text-white"
              >
                {label}
              </NavLink>
            ))}
            <Link
              to="/app"
              onClick={() => setOpen(false)}
              className="py-2.5 text-xs font-black uppercase tracking-widest text-emerald-400"
            >
              Enter as Guest
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

const FOOTER_GROUPS = [
  {
    heading: 'Product',
    links: [
      { to: '/how-it-works', label: 'How It Works' },
      { to: '/features', label: 'Features' },
      { to: '/plus', label: 'LockedIn Plus' },
      { to: '/app', label: 'Enter as Guest' },
    ],
  },
  {
    heading: 'Trust',
    links: [
      { to: '/safety', label: 'Safety & Wallet' },
      { to: '/faq', label: 'FAQ' },
      { to: '/support', label: 'Support' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { to: '/terms', label: 'Terms' },
      { to: '/privacy', label: 'Privacy' },
    ],
  },
];

function PublicFooter() {
  return (
    <footer className="relative z-10 border-t border-white/5 mt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-1">
            <BrandMark />
            <p className="text-xs text-gray-500 font-medium mt-3 leading-relaxed max-w-[16rem]">
              Peer-to-peer sports challenges. Pick a matchup, challenge a friend, let the result decide.
            </p>
          </div>
          {FOOTER_GROUPS.map((group) => (
            <div key={group.heading}>
              <p className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-400/90 mb-3">
                {group.heading}
              </p>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.to + link.label}>
                    <Link to={link.to} className="text-xs font-bold text-gray-400 hover:text-white transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-600">
            © 2026 LockedIn. All rights reserved.
          </p>
          <p className="text-[10px] font-medium text-gray-600 max-w-md text-center sm:text-right">
            LockedIn is a peer-to-peer challenge platform, not a sportsbook. Must meet eligibility requirements to fund a wallet.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <StadiumShell>
      <PublicNav />
      <main className="relative z-10">{children}</main>
      <PublicFooter />
    </StadiumShell>
  );
}

/** Reusable section primitives for marketing pages. */
export function PageHero({ eyebrow, title, subtitle, children }) {
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-10 text-center">
      {eyebrow && (
        <p className="text-[11px] font-black uppercase tracking-[0.28em] text-emerald-400 mb-4">{eyebrow}</p>
      )}
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08] mb-5">
        {title}
      </h1>
      {subtitle && (
        <p className="max-w-2xl mx-auto text-gray-400 text-base sm:text-lg font-medium leading-relaxed">
          {subtitle}
        </p>
      )}
      {children && <div className="mt-8">{children}</div>}
    </section>
  );
}

export function CtaRow({ className = '' }) {
  return (
    <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 ${className}`}>
      <Link
        to="/app"
        className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-black font-black px-10 py-4 rounded-2xl text-sm uppercase tracking-[0.2em] shadow-[0_20px_50px_rgba(16,185,129,0.35)] transition-all active:scale-95 hover:-translate-y-0.5 text-center"
      >
        Enter as Guest
      </Link>
      <Link
        to="/signup"
        className="w-full sm:w-auto px-10 py-4 rounded-2xl text-sm font-black uppercase tracking-[0.2em] border border-white/15 text-white hover:border-emerald-500/40 hover:bg-white/5 transition-all text-center"
      >
        Create Account
      </Link>
    </div>
  );
}
